import React from 'react'
import { Button, Input, Label, Table, Td, Th, Tr, Badge } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreVertical } from 'lucide-react'
import { listRevshareParticipants, rebalanceRevshareShares, upsertRevshareParticipant, type RevshareParticipant } from '../services/revshareParticipantsService'
import { bpsToPercent } from '../utils/percent'
import { Link } from 'react-router-dom'

type Props = {
  refreshKey: number
}

function statusTone(status: string): 'success' | 'warning' | 'violet' {
  if (status === 'active') return 'success'
  if (status === 'suspended') return 'warning'
  return 'violet'
}

export default function RevshareParticipantsTab({ refreshKey }: Props) {
  const [rows, setRows] = React.useState<RevshareParticipant[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const [email, setEmail] = React.useState('')
  const [sharePercent, setSharePercent] = React.useState<number>(5)
  const [submitting, setSubmitting] = React.useState(false)

  const [shareDraft, setShareDraft] = React.useState<Record<string, number>>({})
  const [savingShares, setSavingShares] = React.useState(false)

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listRevshareParticipants()
      setRows(list)
      const map: Record<string, number> = {}
      for (const p of list) {
        map[p.user_id] = Number(p.share_bps || 0) / 100
      }
      setShareDraft(map)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar participantes')
      setRows([])
      setShareDraft({})
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { load() }, [load, refreshKey])

  const allocatedBps = rows
    .filter((p) => p.status === 'active')
    .reduce((sum, p) => sum + Number(p.share_bps || 0), 0)
  const remainingBps = Math.max(0, 10000 - allocatedBps)

  const hasDirtyShares = rows.some((p) => {
    if (p.status !== 'active') return false
    const draft = shareDraft[p.user_id]
    return Math.round((draft || 0) * 100) !== Math.round(Number(p.share_bps || 0))
  })

  async function onAddParticipant() {
    setSubmitting(true)
    setError(null)
    try {
      await upsertRevshareParticipant({
        email: email.trim().toLowerCase(),
        share_percent: Number(sharePercent || 0),
        status: 'active',
      })
      setEmail('')
      setSharePercent(5)
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao adicionar participante')
    } finally {
      setSubmitting(false)
    }
  }

  async function onSaveShares() {
    setSavingShares(true)
    setError(null)
    try {
      const shares = rows
        .filter((p) => p.status === 'active')
        .map((p) => ({ user_id: p.user_id, share_percent: Number(shareDraft[p.user_id] || 0) }))
      await rebalanceRevshareShares({ shares })
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar shares')
    } finally {
      setSavingShares(false)
    }
  }

  async function setStatus(userId: string, status: 'active' | 'suspended' | 'removed') {
    if (status === 'removed' && !confirm('Remover este participante? Ele para de gerar comissao a partir de agora.')) {
      return
    }
    if (status === 'suspended' && !confirm('Suspender este participante? Ele para de gerar comissao ate ser reativado.')) {
      return
    }
    setError(null)
    try {
      await upsertRevshareParticipant({ user_id: userId, status })
      await load()
    } catch (e: any) {
      setError(e?.message || 'Falha ao atualizar status')
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-base font-medium">Participantes</div>
            <div className="text-sm text-foreground/70">
              Shares alocados: <span className="font-mono">{bpsToPercent(allocatedBps, 2)}</span> • Disponivel:{' '}
              <span className="font-mono">{bpsToPercent(remainingBps, 2)}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="space-y-1">
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@dominio.com" className="h-10 w-[260px]" />
            </div>
            <div className="space-y-1">
              <Label>% da pool</Label>
              <Input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={sharePercent}
                onChange={(e) => setSharePercent(Number((e.target as HTMLInputElement).value || 0))}
                className="h-10 w-[140px]"
              />
            </div>
            <Button onClick={onAddParticipant} disabled={submitting || !email.trim()}>
              {submitting ? 'Adicionando...' : 'Adicionar'}
            </Button>
            <Button variant="outline" onClick={onSaveShares} disabled={!hasDirtyShares || savingShares}>
              {savingShares ? 'Salvando...' : 'Salvar shares'}
            </Button>
          </div>
        </div>
        {error && (
          <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-foreground/70">Carregando...</div>
      ) : (
        <Table>
          <thead>
            <Tr>
              <Th>Status</Th>
              <Th>Usuario</Th>
              <Th>% da pool</Th>
              <Th>Ativo desde</Th>
              <Th>Inativo em</Th>
              <Th className="w-44 text-right">Acoes</Th>
            </Tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const sharePct = p.status === 'active' ? Number(shareDraft[p.user_id] || 0) : Number(p.share_bps || 0) / 100
              return (
                <Tr key={p.user_id}>
                  <Td>
                    <Badge variant="soft" color={statusTone(p.status) as any} className="capitalize">
                      {p.status}
                    </Badge>
                  </Td>
                  <Td>
                    <div className="flex flex-col">
                      <span className="font-medium">{p.full_name || '-'}</span>
                      <span className="text-xs text-foreground/70">{p.email || p.user_id}</span>
                    </div>
                  </Td>
                  <Td>
                    {p.status === 'active' ? (
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={sharePct}
                        onChange={(e) => {
                          const v = Number((e.target as HTMLInputElement).value || 0)
                          setShareDraft((prev) => ({ ...prev, [p.user_id]: v }))
                        }}
                        className="h-9 w-[140px]"
                      />
                    ) : (
                      <span className="font-mono">{bpsToPercent(p.share_bps, 2)}</span>
                    )}
                  </Td>
                  <Td className="text-sm text-foreground/80">{p.active_from ? new Date(p.active_from).toLocaleString() : '-'}</Td>
                  <Td className="text-sm text-foreground/80">{p.inactive_at ? new Date(p.inactive_at).toLocaleString() : '-'}</Td>
                  <Td className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button asChild variant="outline" className="h-8 px-2 text-xs">
                        <Link to={`/repasses/${p.user_id}`}>Detalhes</Link>
                      </Button>
                      <DropdownMenu
                        align="end"
                        trigger={
                          <Button variant="outline" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        }
                        items={[
                          ...(p.status !== 'active'
                            ? [{ label: 'Ativar', onSelect: () => setStatus(p.user_id, 'active' as any) }]
                            : []),
                          ...(p.status === 'active'
                            ? [{ label: 'Suspender', onSelect: () => setStatus(p.user_id, 'suspended' as any) }]
                            : []),
                          { label: <span className="text-destructive">Remover</span>, onSelect: () => setStatus(p.user_id, 'removed' as any) },
                        ]}
                      />
                    </div>
                  </Td>
                </Tr>
              )
            })}
            {rows.length === 0 && (
              <Tr>
                <Td colSpan={6} className="py-6 text-center text-sm text-foreground/70">
                  Nenhum participante cadastrado.
                </Td>
              </Tr>
            )}
          </tbody>
        </Table>
      )}
    </div>
  )
}
